import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGUAGES } from './constants';
import { getTranslatedPath } from './urlHelpers';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const MAPBOX_SDK_SCRIPT_ID = 'mapbox_SDK_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';
const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID;

// Route names (with their canonical English fallback path) that render a Map
// or a LocationAutocompleteInput, and therefore need the map provider's
// script loaded. Non-English locales serve these under fully translated URL
// segments (e.g. /fr/recherche, /es/busqueda, /zh/搜索 for SearchPage) rather
// than the English words below, so the actual path fragments to match are
// derived per-locale from TRANSLATED_PATHS (see urlHelpers.js) instead of
// hardcoding English-only substrings.
const MAP_ROUTE_DEFS = [
  { name: 'SearchPage', fallbackPath: '/search' },
  { name: 'ListingBasePage', fallbackPath: '/listing' },
  { name: 'ListingPage', fallbackPath: '/rentals/:slug/:id' },
  { name: 'CheckoutPageOld', fallbackPath: '/l/:slug/:id/checkout' },
  { name: 'CheckoutPage', fallbackPath: '/listing/:slug/:id/checkout' },
  { name: 'ListingPageVariantOld', fallbackPath: '/l/:slug/:id/:variant' },
  { name: 'ProfilePage', fallbackPath: '/rental-owner/:city/:slug/:id' },
  { name: 'ProfilePageNoCity', fallbackPath: '/rental-owner/:slug/:id' },
  { name: 'ProfileSettingsPage', fallbackPath: '/profile-settings' },
  {
    name: 'RentAnythingWizardPage',
    fallbackPath: '/rentanything-wizard/listing/:action/:step/:listingToken?',
  },
  { name: 'PublishedListingPage', fallbackPath: '/published-listing/:slug/:listingId' },
  { name: 'StoreSettingsPage', fallbackPath: '/store-settings' },
];

// Flattened, deduped set of fixed (non-param) path prefixes across all
// supported locales, e.g. '/search', '/recherche', '/busqueda', '/搜索'.
// Keep only the fixed (non-param) prefix of a route path, e.g.
// '/rentals/:slug/:id' -> '/rentals/'. The trailing slash is kept
// deliberately: without it, a short prefix like '/l' (from '/l/:slug/:id')
// would also match unrelated paths such as '/login'.
const staticPathPrefix = path => {
  const paramIndex = path.indexOf('/:');
  return paramIndex === -1 ? path : path.slice(0, paramIndex + 1);
};

const MAP_SCRIPT_PATHS = Array.from(
  new Set(
    MAP_ROUTE_DEFS.flatMap(({ name, fallbackPath }) =>
      SUPPORTED_LANGUAGES.map(lang =>
        staticPathPrefix(getTranslatedPath(name, fallbackPath, lang))
      )
    )
  )
).filter(Boolean);

export const shouldLoadMapScriptsForPath = pathname => {
  if (!pathname) {
    return true;
  }

  return MAP_SCRIPT_PATHS.some(path => pathname.includes(path));
};

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains } = analytics;
  const location = useLocation();
  const pathname =
    props?.locationPath ||
    location?.pathname ||
    (typeof window !== 'undefined' ? window.location.pathname : null);

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';
  const shouldLoadMapScripts = shouldLoadMapScriptsForPath(pathname);

  useEffect(() => {
    if (!shouldLoadMapScripts || typeof document === 'undefined') {
      return;
    }

    const appendScript = (id, src, onLoad) => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        if (onLoad && existingScript.dataset.loaded === 'true') {
          onLoad();
        }
        return;
      }

      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        script.dataset.loaded = 'true';
        if (onLoad) {
          onLoad();
        }
      };
      document.head.appendChild(script);
    };

    const signalMapboxReady = () => {
      if (window.mapboxgl && mapboxAccessToken && !window.mapboxgl.accessToken) {
        window.mapboxgl.accessToken = mapboxAccessToken;
      }

      if (window.mapboxgl && window.mapboxSdk) {
        try {
          window.dispatchEvent(new Event('mapboxgl:ready'));
          window.dispatchEvent(new Event('mapboxsdk:ready'));
        } catch (e) {
          // Ignore event dispatch failures in older browsers.
        }
      }
    };

    if (isMapboxInUse) {
      appendScript(
        MAPBOX_SDK_SCRIPT_ID,
        `${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`,
        signalMapboxReady
      );
      appendScript(
        MAPBOX_SCRIPT_ID,
        'https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.js',
        signalMapboxReady
      );
    } else if (isGoogleMapsInUse && googleMapsAPIKey) {
      appendScript(
        GOOGLE_MAPS_SCRIPT_ID,
        `https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`
      );
    }
  }, [
    googleMapsAPIKey,
    isGoogleMapsInUse,
    isMapboxInUse,
    mapboxAccessToken,
    rootURL,
    shouldLoadMapScripts,
  ]);

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  if (isMapboxInUse && shouldLoadMapScripts) {
    // CSS must be in <head> before the map initialises; dynamic injection in
    // componentDidMount races against DynamicMapboxMap's own componentDidMount
    // and triggers Mapbox GL JS's "missing CSS" warning.
    mapLibraries.push(
      <link
        key="mapbox_GL_CSS"
        rel="stylesheet"
        href="https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.css"
        crossOrigin="anonymous"
      />
    );

    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    mapLibraries.push(
      <script id={MAPBOX_SDK_SCRIPT_ID} key="mapboxSDK" src={`${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`} defer></script>
    );

    // Add Mapbox library
    mapLibraries.push(
      <script
        id={MAPBOX_SCRIPT_ID}
        key="mapbox_GL_JS"
        src="https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.js"
        defer
        crossOrigin
      ></script>
    );
  } else if (isGoogleMapsInUse && shouldLoadMapScripts) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      ></script>
    );
  }

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js - Deferred loading after main content
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script key="gtag.js">
        {`(function() {
          function loadGoogleAnalytics() {
            // Load gtag script
            var script = document.createElement('script');
            script.async = true;
            script.src = 'https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}';
            script.crossOrigin = 'anonymous';
            document.head.appendChild(script);
            
            // Initialize gtag
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${googleAnalyticsId}', {
              cookie_flags: 'SameSite=None;Secure',
            });
            window.gtag = gtag;
          }
          
          // Load after main content is ready
          if (document.readyState === 'complete') {
            // Use requestIdleCallback if available, otherwise setTimeout
            if (window.requestIdleCallback) {
              requestIdleCallback(loadGoogleAnalytics, { timeout: 2000 });
            } else {
              setTimeout(loadGoogleAnalytics, 100);
            }
          } else {
            window.addEventListener('load', function() {
              if (window.requestIdleCallback) {
                requestIdleCallback(loadGoogleAnalytics, { timeout: 2000 });
              } else {
                setTimeout(loadGoogleAnalytics, 100);
              }
            });
          }
        })();`}
      </script>
    );
  }



  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin
      ></script>
    );
  }

  const isBrowser = typeof window !== 'undefined';
  const isMapboxLoaded = isBrowser && window.mapboxgl;

  // If Mapbox is loaded, we can set the accessToken already here.
  // This is the execution flow with the production build,
  // since SSR includes those map libraries to <head> of the app.
  if (isMapboxInUse && isMapboxLoaded && !window.mapboxgl.accessToken) {
    // Add access token for Mapbox library
    window.mapboxgl.accessToken = mapboxAccessToken;
    // Signal readiness for components that might wait
    try { window.dispatchEvent(new Event('mapboxgl:ready')); } catch (e) { }
    if (window.mapboxSdk) {
      try { window.dispatchEvent(new Event('mapboxsdk:ready')); } catch (e) { }
    }
  }

  // If the script is added on client side as a reaction to page navigation or
  // the app is rendered on client side entirely (e.g. HMR/WebpackDevServer),
  // we need to listen when the script is loaded.
  const onGlLoaded = () => {
    if (!isMapboxInUse || typeof window === 'undefined' || !window.mapboxgl) return;
    if (!window.mapboxgl.accessToken) {
      window.mapboxgl.accessToken = mapboxAccessToken;
    }
    try { window.dispatchEvent(new Event('mapboxgl:ready')); } catch (e) { }
  };

  const onSdkLoaded = () => {
    if (!isMapboxInUse || typeof window === 'undefined' || !window.mapboxSdk) return;
    try { window.dispatchEvent(new Event('mapboxsdk:ready')); } catch (e) { }
  };

  // React Helmet Async doesn't support onLoad prop for scripts.
  // However, it does have onChangeClientState functionality.
  // We can use that to start listen 'load' events when the library is added on client-side.
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      addedTags.scriptTags.forEach(s => {
        if (s.id === MAPBOX_SCRIPT_ID) {
          s.addEventListener('load', onGlLoaded, { once: true });
        } else if (s.id === MAPBOX_SDK_SCRIPT_ID) {
          s.addEventListener('load', onSdkLoaded, { once: true });
        }
      });
    }
  };

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{allScripts}</Helmet>;
};
